/**
 * Plan tablosu — RAKAMLARIN TEK KAYNAĞI.
 *
 * Tasarım dokümanı §7 (Abonelik Planları) ve §8 (Kredi Sistemi).
 *
 * ⚠️ Bu rakamlar HİPOTEZ. Dokümanın kendi uyarısı (§7) ve açık konular
 * listesi (§15, madde 3) fiyatların beta geri bildirimiyle kalibre
 * edileceğini söylüyor. Bu yüzden hiçbir sayı bileşenlerde ya da çeviri
 * dosyalarında tekrarlanmaz — hepsi buradan okunur.
 *
 * ---------------------------------------------------------------------------
 * Neden `apps/web/src/lib/` değil de burası
 * ---------------------------------------------------------------------------
 * Kayıt akışı abonelik satırını açarken limitleri buradan kopyalıyor
 * (`Subscription.adSpendLimit` vb.). Tablo yalnızca frontend'de kalsaydı
 * backend aynı sayıları ikinci kez tanımlamak zorunda kalır, ikisi de
 * "tek kaynak" iddia eder ve er geç ayrışırdı.
 *
 * ---------------------------------------------------------------------------
 * 🔴 FAZ 8: ARTIK TEK KAYNAK DEĞİL — VARSAYILAN
 * ---------------------------------------------------------------------------
 * Rakamlar `plan_catalog` TABLOSUNA taşındı ve `/admin/plans` ekranından
 * düzenleniyor. Bu dizi artık ÇALIŞMA ZAMANI KAYNAĞI değil, TABLO BOŞKEN ya
 * da bir satır eksikken düşülen varsayılan.
 *
 * Okuma yolu tek: `apps/api/src/billing/plan-catalog.service.ts`. Buradaki
 * sayıyı değiştirmek, tabloda karşılığı OLMAYAN kademeleri etkiler; tabloda
 * satırı olan kademe bu değişikliği GÖRMEZ. Yönün neden böyle seçildiği o
 * dosyanın başında yazılı.
 *
 * `limitsForTier` (aşağıda) hâlâ buradan okuyor ve bu bilinçli: kayıt akışı
 * abonelik satırını açarken veritabanına bağımlı olmamalı — katalog
 * okunamadığında yeni kullanıcı kaydı düşmemeli.
 */

export type PlanId = 'starter' | 'growth' | 'pro' | 'enterprise';

/** Sayısal kota ya da "plana özel" / "sınırsız" — Enterprise için gerekli. */
export type Quota = number | 'custom' | 'unlimited';

export interface Plan {
  id: PlanId;
  /** Aylık liste fiyatı, USD. `null` = özel fiyat (Enterprise). */
  monthlyUsd: number | null;
  /**
   * Fatura döneminde senkronlanan reklam harcamasının üst sınırı, USD.
   * `null` = üstü açık; o durumda `spendFloorUsd` alt sınırı verir.
   */
  spendCapUsd: number | null;
  /** Yalnızca üstü açık planda dolu: "şu tutarın üzeri". */
  spendFloorUsd: number | null;
  /** Dönem başında yüklenen AI kredisi; devretmez (§8). */
  credits: Quota;
  /** Bağlanabilen reklam hesabı sayısı. */
  accounts: Quota;
  /** Çalışma alanına eklenebilen ekip üyesi sayısı. */
  members: Quota;
}

/**
 * Sıra hem fiyat sıralaması hem de harcama ölçeğinin sırası — anasayfadaki
 * ölçek bileşeni doğrudan bu diziyi geziyor. Yeni plan araya girerse ölçek
 * de kendiliğinden büyür.
 */
export const PLANS: readonly Plan[] = [
  {
    id: 'starter',
    monthlyUsd: 19,
    spendCapUsd: 2_500,
    spendFloorUsd: null,
    credits: 50,
    accounts: 3,
    members: 2,
  },
  {
    id: 'growth',
    monthlyUsd: 49,
    spendCapUsd: 10_000,
    spendFloorUsd: null,
    credits: 150,
    accounts: 10,
    members: 5,
  },
  {
    id: 'pro',
    monthlyUsd: 119,
    spendCapUsd: 50_000,
    spendFloorUsd: null,
    credits: 500,
    accounts: 25,
    members: 15,
  },
  {
    id: 'enterprise',
    monthlyUsd: null,
    spendCapUsd: null,
    spendFloorUsd: 50_000,
    credits: 'custom',
    accounts: 'unlimited',
    members: 'unlimited',
  },
] as const;

/**
 * Sıradaki planı güvenle verir.
 *
 * `noUncheckedIndexedAccess` açık: `PLANS[i]` tip olarak `Plan | undefined`.
 * Harcama ölçeği indeksi state'te tuttuğu için her okumada elle kontrol
 * yazmak yerine sınır burada bir kez uygulanıyor.
 */
export function planAt(index: number): Plan {
  const plan = PLANS[Math.min(Math.max(index, 0), PLANS.length - 1)];
  if (!plan) throw new Error('PLANS boş olamaz — en az bir plan tanımlı olmalı.');
  return plan;
}

/** Deneme sürümü — plan değil, her planın başlangıcı. Kart istenmez (§7). */
export const TRIAL = {
  days: 7,
  spendCapUsd: 2_500,
  credits: 25,
  accounts: 2,
  members: 1,
} as const;

/** Yıllık ödemede indirim oranı (§7). */
export const ANNUAL_DISCOUNT = 0.2;

/**
 * Yumuşak sınır: limit aşımında uygulama kilitlenmez, önce uyarı ve bu kadar
 * gün ek süre verilir. Sonrasında yalnızca otomasyon ve kreatif üretim
 * duraklar — dashboard her koşulda açık kalır (§7).
 */
export const GRACE_DAYS = 7;

/**
 * Yıllık ödemede aylığa düşen tutar. Yuvarlama YOK: $19 → $15.20.
 * Yuvarlasaydık ekranda gösterilen rakam ödeme sağlayıcısının çekeceği
 * tutardan sapardı; fiyat konusunda görünen ile ödenen aynı olmalı.
 */
export function annualMonthlyUsd(monthlyUsd: number): number {
  return monthlyUsd * (1 - ANNUAL_DISCOUNT);
}

// ─────────────────────── Abonelik satırına kopyalanan limitler ───────────────────────

/** Prisma `PlanTier` enum'unun ayna hâli (frontend Prisma client'ı import etmiyor). */
export const PlanTier = {
  TRIAL: 'TRIAL',
  STARTER: 'STARTER',
  GROWTH: 'GROWTH',
  PRO: 'PRO',
  ENTERPRISE: 'ENTERPRISE',
} as const;
export type PlanTier = (typeof PlanTier)[keyof typeof PlanTier];

/**
 * Abonelik açılırken satıra yazılan sayısal limitler.
 *
 * Neden kopyalanıyor: `Subscription` şemasındaki yorumun dediği gibi, plan
 * fiyatları/limitleri sonradan değişirse mevcut abonelerin limiti kaymasın.
 * Kullanıcı $49'a aldığı kotayı, biz tabloyu değiştirdik diye kaybetmemeli.
 *
 * `unlimited` / `custom` burada -1 olur: Enterprise'ın limiti sözleşmede
 * tanımlı, uygulamada değil. Kontrol eden kod negatif değeri "sınır yok"
 * olarak okur.
 */
export interface SubscriptionLimits {
  adSpendLimit: number;
  monthlyCredits: number;
  maxAdAccounts: number;
  maxMembers: number;
}

const UNLIMITED = -1;

function quotaToNumber(q: Quota): number {
  return typeof q === 'number' ? q : UNLIMITED;
}

export function limitsForTier(tier: PlanTier): SubscriptionLimits {
  if (tier === PlanTier.TRIAL) {
    return {
      adSpendLimit: TRIAL.spendCapUsd,
      monthlyCredits: TRIAL.credits,
      maxAdAccounts: TRIAL.accounts,
      maxMembers: TRIAL.members,
    };
  }

  const plan = PLANS.find((p) => p.id === tier.toLowerCase());
  if (!plan) throw new Error(`Bilinmeyen plan kademesi: ${tier}`);

  return {
    adSpendLimit: plan.spendCapUsd ?? UNLIMITED,
    monthlyCredits: quotaToNumber(plan.credits),
    maxAdAccounts: quotaToNumber(plan.accounts),
    maxMembers: quotaToNumber(plan.members),
  };
}

/** Limit değeri sınırsızı mı gösteriyor. */
export function isUnlimited(value: number): boolean {
  return value < 0;
}

/**
 * Bir sonraki dönem sonu.
 *
 * Ay ekleme takvimsel yapılıyor (30 gün değil): 31 Ocak'ta başlayan dönem
 * 28 Şubat'ta biter, 2 Mart'ta değil. `setMonth` ayın son gününü taşırdığı
 * için gün sayısı sınırlanıyor — yoksa 31 Ocak + 1 ay = 3 Mart olur ve
 * kullanıcı iki gün fazladan kota alır.
 *
 * ---------------------------------------------------------------------------
 * Neden `shared` — worker'ın bakım işinin içinde değil
 * ---------------------------------------------------------------------------
 * Dönemi ilerleten iki yer var: bakım işi (`credit-rollover.service.ts`) ve
 * API'nin ödeme dönüşü — sağlayıcı dönem tarihi bildirmediğinde abonelik
 * satırını kendisi açması gerekiyor. İkisi ayrı hesap yapsaydı aynı kiracının
 * dönem sonu, satırı kimin yazdığına göre bir gün kayardı; kredi yükleme
 * anahtarı dönem başlangıcını içerdiği için bu, aynı aya iki kez kredi
 * yüklenmesi demek olurdu.
 */
export function nextPeriodEnd(from: Date): Date {
  const next = new Date(from.getTime());
  const day = next.getUTCDate();
  next.setUTCMonth(next.getUTCMonth() + 1);
  if (next.getUTCDate() < day) {
    // Taşma olmuş (31 → 3): ayın son gününe çek.
    next.setUTCDate(0);
  }
  return next;
}
